import { createContext, useMemo } from 'react';

import { useDisclosure } from '@chakra-ui/react';

import { useQuery } from '@tanstack/react-query';

import { authService, CognitoUser, setApiResponseInterceptor } from '@aslanpay/portal-common-lib';

import NotAuthorizedModal from 'app/components/UI/NotAuthorizedModal';

import useFetchIndividual from 'app/hooks/useFetchIndividual';

import { AuthContextProps, AuthContextProviderProps } from './types';

export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export function AuthContextProvider({ children }: AuthContextProviderProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: cognitoUser, isLoading: isValidatingAuth } = useQuery<CognitoUser>(
    ['getAuthenticatedUser'],
    () => authService.getAuthenticatedUser()
  );

  const { data: user, isInitialLoading: isFetchingIndividual } = useFetchIndividual(
    cognitoUser?.attributes && cognitoUser?.attributes['custom:individual_id']
  );

  if (user) {
    setApiResponseInterceptor(() => onOpen());
  }

  const value = useMemo(
    () => ({
      user,
      cognitoUser,
      isValidatingAuth,
      isFetchingIndividual,
    }),
    [user, cognitoUser, isValidatingAuth, isFetchingIndividual]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      <NotAuthorizedModal isOpen={isOpen} onClose={onClose} />
    </AuthContext.Provider>
  );
}
